import React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Box, keyframes, styled } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

interface PasswordRequirementsProps {
	password: string;
}

// Shake animation keyframes
const shake = keyframes`
  0% { transform: translateX(0px); }
  20% { transform: translateX(-2px); }
  40% { transform: translateX(2px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
  100% { transform: translateX(0px); }
`;

// Styled component with transition for color change
const AnimatedListItemIcon = styled(ListItemIcon)(({ theme }) => ({
	transition: theme.transitions.create('color', {
		duration: theme.transitions.duration.standard,
	}),
	'&.shake': {
		animation: `${shake} 0.5s ease-in-out`,
	},
}));

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ password }) => {
	// Password criteria checks
	const isLengthValid = password.length >= 8;
	const hasAlphabetic = /^(?=.*[a-z])(?=.*[A-Z]).+$/.test(password);
	const hasNumber = /\d/.test(password);
	const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`£€]/.test(password);

	const checkIconColor = (condition: boolean) => (condition ? 'success' : 'disabled');
	const iconStyle = { height: '40px', width: '40px' };
	return (
		<Box
			sx={{
				mb: 3,
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				mx: 'auto',
				p: 2,
			}}
		>
			<Typography gutterBottom textAlign='left'>
				Now its time to set your password, we require the password to be:
			</Typography>
			<List dense>
				<ListItem>
					<ListItemIcon>
						<AnimatedListItemIcon>
							<CheckCircle color={checkIconColor(isLengthValid)} sx={iconStyle} />
						</AnimatedListItemIcon>
					</ListItemIcon>
					<ListItemText primary='At least 8 characters in length' />
				</ListItem>
				<ListItem>
					<AnimatedListItemIcon>
						<CheckCircle color={checkIconColor(hasAlphabetic)} sx={iconStyle} />
					</AnimatedListItemIcon>
					<ListItemText primary='Includes one uppercase and one lowercase letter (a-z, A-Z)' />
				</ListItem>
				<ListItem>
					<AnimatedListItemIcon>
						<CheckCircle color={checkIconColor(hasNumber)} sx={iconStyle} />
					</AnimatedListItemIcon>
					<ListItemText primary='Includes at least one number (0-9)' />
				</ListItem>
				<ListItem>
					<AnimatedListItemIcon>
						<CheckCircle color={checkIconColor(hasSpecialChar)} sx={iconStyle} />
					</AnimatedListItemIcon>
					<ListItemText primary='Has at least one special character (e.g.,!@#$%^&* and others)' />
				</ListItem>
			</List>
		</Box>
	);
};

export default PasswordRequirements;
